import React from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {Link, useBreakpointValue} from '@chakra-ui/react'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import PDCImage from '../../../components/pdc-image/index.jsx'
import {useUrlFromLink} from '../../helpers/urlHelper.js'

/**
 * PDCCarouselImageGalleryTile is a React functional component that renders a responsive image tile for use within a carousel image gallery.
 * It dynamically selects the appropriate image source based on the current viewport breakpoint and the provided image URLs for different sizes.
 * The component also adjusts the displayed image's width according to the number of slides visible in the carousel, ensuring optimal layout across devices.
 * When a link is configured, the image is wrapped in a link that can point to an external URL, a category, a Page Designer page, a product or a search term, and can optionally be opened in a new tab.
 *
 * Props:
 * @param {string} smallImage - URL for the small breakpoint image.
 * @param {string} mediumImage - URL for the medium breakpoint image.
 * @param {string} largeImage - URL for the large breakpoint image.
 * @param {string} xlargeImage - URL for the extra-large breakpoint image.
 * @param {string} xxlargeImage - URL for the double extra-large breakpoint image.
 * @param {string} imageTitle - Title attribute for the image, used for accessibility and tooltips.
 * @param {string} imageAlt - Alt text for the image, used for accessibility.
 * @param {number} displayPriority - Priority value to control image loading behavior.
 * @param {number} nbSlides - Number of slides visible in the carousel, used to determine image width.
 * @param {string} link - Optional fallback URL used when no custom link structure is provided.
 * @param {boolean} isExternal - When true, the link is opened in a new tab ("Is External" option).
 * @param {function} onImageLoad - Callback function triggered when the image finishes loading.
 * @param {object} custom - Optional custom image sources for each breakpoint, with `disBaseLink` properties, and the `link` structure.
 *
 * The component leverages responsive utilities to determine the correct image to display and passes all relevant props to the underlying PDCImage component.
 * It also configures skeleton loading and ensures the image maintains the correct aspect ratio and sizing within the carousel context.
 */
export const PDCCarouselImageGalleryTile = ({
    smallImage,
    mediumImage,
    largeImage,
    xlargeImage,
    xxlargeImage,
    imageTitle,
    imageAlt,
    displayPriority,
    nbSlides,
    link,
    isExternal,
    onImageLoad,
    custom
}) => {
    const updatedUrl = useUrlFromLink(custom?.link) || link
    const isLinkExternal = custom?.link?.type === 'EXTERNAL' && !updatedUrl?.startsWith('/')
    const openInNewTab = custom?.isExternal ?? isExternal
    // Get current image based on breakpoint
    const currentImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumImage,
            largeEl: custom?.largeImage?.disBaseLink || largeImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeImage
        }),
        {
            fallback: undefined
        }
    )

    let imageWidths
    switch (nbSlides) {
        case 1:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.full
            break
        case 2:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
        case 3:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.thirdandfullonsmalldevice
            break
        case 4:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.quarterandfullonsmalldevice
            break
        default:
            imageWidths = DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice
            break
    }

    const image = (
        <PDCImage
            currentImage={currentImage}
            imageWidths={imageWidths}
            imageAlt={imageAlt}
            imageTitle={imageTitle}
            imageProps={{height: '100%'}}
            displayPriority={displayPriority}
            skeletonProps={{aspectRatio: '4 / 5', width: '100%'}}
            position="relative"
            height={'100%'}
            width="auto"
            onImageLoad={onImageLoad}
        />
    )

    // The link is optional. When configured, wrap the image so the whole tile is clickable.
    if (updatedUrl) {
        return (
            <Link
                as={isLinkExternal ? undefined : RouteLink}
                href={updatedUrl}
                to={isLinkExternal ? undefined : updatedUrl}
                display="block"
                height="100%"
                target={openInNewTab ? '_blank' : undefined}
                rel={openInNewTab ? 'noopener noreferrer' : undefined}
            >
                {image}
            </Link>
        )
    }

    return image
}

PDCCarouselImageGalleryTile.propTypes = {
    category: PropTypes.string,
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    txtVerticalPos: PropTypes.string,
    smallImage: PropTypes.object.isRequired,
    mediumImage: PropTypes.object,
    largeImage: PropTypes.object,
    xlargeImage: PropTypes.object,
    xxlargeImage: PropTypes.object,
    imageTitle: PropTypes.string,
    imageAlt: PropTypes.string,
    displayPriority: PropTypes.bool,
    nbSlides: PropTypes.number,
    link: PropTypes.string,
    isExternal: PropTypes.bool,
    custom: PropTypes.object,
    onImageLoad: PropTypes.func
}
